import React from "react";

import { useTranslation } from "react-i18next";

import { TypeEnum } from "../../../api/ProjectAPI.types";
import SwitchOnOff from "../../common/tools/SwitchOnOff";

import type { ChangeEvent, JSX } from "react";

import type {
  Project,
  StorefrontProjectLoginConfig,
  UpdatedProject,
} from "../../../api/ProjectAPI.types";

interface ProjectAdminFormDescriptionProps {
  project: Project | UpdatedProject;
  setProject: (project: Project | UpdatedProject) => void;
  isNew: boolean;
  onChangeStorefrontProjectLoginAttr: (
    target: ChangeEvent<HTMLInputElement>["target"],
    socialNetworkName: string,
    attr: keyof StorefrontProjectLoginConfig,
    defaultConfig: StorefrontProjectLoginConfig,
  ) => void;
}

function ProjectAdminFormUrl({
  project,
  setProject,
  isNew,
  onChangeStorefrontProjectLoginAttr,
}: ProjectAdminFormDescriptionProps): JSX.Element {
  const { t } = useTranslation("editor-project");

  const handleChangeName = (value: string): void => {
    setProject({
      ...project,
      name: value,
    });
  };

  const handleChangeProjectType = (checked: boolean): void => {
    setProject({
      ...project,
      type: checked ? TypeEnum.Edu : TypeEnum.Standard,
    });
  };

  const eduLibLynx = project.storefront_project_logins.find(
    (storefrontProjectLogin) =>
      storefrontProjectLogin.login_provider === "edu_liblynx",
  ) ?? {
    config: {
      client_id: "",
      client_secret: "",
    },
    login_provider: "",
  };

  return (
    <React.Fragment>
      <div className="editor-form-section section-separator">
        <div className="editor-form-translates">
          <div className="inline-block">
            <div className="bold">{t("url")}</div>
            {project.domain_alias ? (
              <div className="bold">: {project.domain_alias}</div>
            ) : (
              <React.Fragment>
                <input
                  className="editor-form-input-title"
                  disabled={!isNew}
                  maxLength={128}
                  type="text"
                  value={project.name}
                  onChange={(e): void => {
                    handleChangeName(e.target.value);
                  }}
                />
                <div className="bold">{t("domain")}</div>
              </React.Fragment>
            )}
            {isNew ? <div className="italic">{t("domainHint")}</div> : null}
          </div>
          {project.domain_alias ? (
            <div className="italic">{t("customDomainConfigured")}</div>
          ) : (
            <div className="italic">{t("customDomain")}</div>
          )}
        </div>
      </div>
      <div className="editor-form-section section-separator">
        <div className="editor-form-translates">
          <div className="editor-form-section-title">{t("projectType")}</div>
          <div className="editor-form-columns">
            <div className="project-type-switch">
              <div className="switch-label">{t("projectTypeStandard")}</div>
              <SwitchOnOff
                checked={project.type === TypeEnum.Edu}
                disabled={!isNew}
                name="type-switch"
                onChange={handleChangeProjectType}
                size="sm"
                iconName={
                  project.type === TypeEnum.Edu ? "domain" : "local_activity"
                }
              />
              <div className="switch-label">{t("projectTypeEdu")}</div>
            </div>
            {project.type === TypeEnum.Edu && (
              <div className="editor-form-translates">
                <div className="editor-form-translate">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>{t("appId")}</label>
                  <input
                    className="editor-form-input-title"
                    type="text"
                    value={eduLibLynx.config.client_id}
                    onChange={(e): void => {
                      onChangeStorefrontProjectLoginAttr(
                        e.target,
                        "edu_liblynx",
                        "client_id",
                        { client_id: "", client_secret: "" },
                      );
                    }}
                  />
                </div>
                <div className="editor-form-translate">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>{t("appSecret")}</label>
                  <input
                    className="editor-form-input-title"
                    type="text"
                    value={eduLibLynx.config.client_secret}
                    onChange={(e): void => {
                      onChangeStorefrontProjectLoginAttr(
                        e.target,
                        "edu_liblynx",
                        "client_secret",
                        { client_id: "", client_secret: "" },
                      );
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default ProjectAdminFormUrl;
